import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';


// Multi-select dropdown component with grouping support
export const MultiSelectDropdown = ({
    label,
    options,
    selectedItems,
    onToggle,
    loading,
    error,
    disabled,
    groupBy,
    getGroupKey,
    getGroupLabel,
    emptyMessage = 'No items available',
    collapsibleGroups = false,
    formatOptionLabel = null
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});

    useEffect(() => {
        const closeDropdown = () => setIsOpen(false);
        document.addEventListener('neuronova-close-select-dropdowns', closeDropdown);
        return () => {
            document.removeEventListener('neuronova-close-select-dropdowns', closeDropdown);
        };
    }, []);

    // Group options if groupBy is provided
    const groupedOptions = useMemo(() => {
        if (!groupBy || !getGroupKey) {
            return { 'all': options };
        }

        const groups = {};
        options.forEach(option => {
            const groupKey = getGroupKey(option);
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(option);
        });

        return groups;
    }, [options, groupBy, getGroupKey]);

    const getGroupKeys = useMemo(() => {
        return Object.keys(groupedOptions).sort();
    }, [groupedOptions]);

    const displayText = `${label} (${selectedItems.length} selected)`;

    return (
        <div className="multi-select-dropdown-container">
            <button
                className="multi-select-dropdown-trigger"
                onClick={() => setIsOpen(prev => !prev)}
                disabled={disabled || loading}
            >
                <span>{displayText}</span>
                <ChevronDown
                    size={16}
                    className={`multi-select-chevron ${isOpen ? 'open' : ''}`}
                />
            </button>
            {isOpen && (
                <div
                    className="multi-select-dropdown-content"
                >
                    {loading && <div className="dropdown-loading">Loading...</div>}
                    {error && <div className="dropdown-error">Error: {error}</div>}
                    {!loading && !error && options.length === 0 && (
                        <div className="dropdown-empty">{emptyMessage}</div>
                    )}
                    {!loading && !error && options.length > 0 && (
                        <div className="multi-select-dropdown-list">
                            {getGroupKeys.map(groupKey => {
                                const groupOptions = groupedOptions[groupKey];
                                const groupLabel = getGroupLabel ? getGroupLabel(groupKey) : groupKey;
                                const isGrouped = groupBy && getGroupKeys.length > 1;
                                const isExpanded = expandedGroups[groupKey] === true; // Default to collapsed
                                const shouldBeCollapsible = collapsibleGroups && isGrouped;

                                return (
                                    <div key={groupKey} className="multi-select-group">
                                        {isGrouped && (
                                            <div
                                                className={`multi-select-group-header ${shouldBeCollapsible ? 'collapsible' : ''}`}
                                                onClick={shouldBeCollapsible ? () => {
                                                    setExpandedGroups(prev => ({
                                                        ...prev,
                                                        [groupKey]: !isExpanded
                                                    }));
                                                } : undefined}
                                                style={shouldBeCollapsible ? { cursor: 'pointer' } : {}}
                                            >
                                                {shouldBeCollapsible && (
                                                    <ChevronRight
                                                        size={14}
                                                        className={`multi-select-group-chevron ${isExpanded ? 'expanded' : ''}`}
                                                        style={{ marginRight: '6px', flexShrink: 0 }}
                                                    />
                                                )}
                                                <span>{groupLabel}</span>
                                            </div>
                                        )}
                                        {(!shouldBeCollapsible || isExpanded) && groupOptions.map(option => {
                                            const optionId = option.id || option;
                                            let optionLabel = option.label || option.name || option;
                                            // Apply custom formatting if provided
                                            if (formatOptionLabel) {
                                                optionLabel = formatOptionLabel(optionLabel, option);
                                            }
                                            const isSelected = selectedItems.includes(optionId);

                                            return (
                                                <label
                                                    key={optionId}
                                                    className="multi-select-item"
                                                    title={option.description || optionLabel}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => onToggle?.(optionId)}
                                                    />
                                                    <span>
                                                        {optionLabel}
                                                        {option.description && (
                                                            <small className="multi-select-item-detail">{optionId}</small>
                                                        )}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
